import {Injectable}                                                     from '@angular/core';
import {ApStatsType, IBarLineAreaChartData, IBarLineAreaChartDataValue} from '../../ap-interface';
import {Create}                                                         from 'ts-tooling';
import {ChartSetValueType, IChartSet, StatisticStore}                   from '../../stores/statistic/statistic.store';
import {
  TranslationStore
}                                                                       from '../../stores/translation/translation.store';
import {SettingsStore}                                                  from '../../stores/base-data/settings.store';
import {ApDateService}                                                  from '../../ap-core/services/ap-date-service';
import {LandUseStore}                                                   from '../../stores/base-data/landuse.store';
import {
  FieldNutrientDistributionStore
}                                                                       from '../../stores/nutrients/field-nutrient-distributions.store';
import {
  SoilGroupFormatService
}                                                                       from '../../ap-utils/service/soil-group-format.service';
import {
  GetSoilSampleClassColorService
}                                                                       from '../../ap-utils/service/get-soil-sample-class-color.service';
import {GetSoilSampleClassNamePipe}                                     from '../../ap-utils';
import IGuid = System.IGuid;
import IHistogram = Data.Nutrients.IHistogram;

export interface IFieldStatisticHistogramData {
  Status: number;
  FieldId: IGuid;
  FieldName: string;
  AreaHa: number;
  Histograms: IHistogram[];
}

@Injectable({providedIn: 'root'})
export class FieldStatisticHistogramService {
  constructor(private settings: SettingsStore,
              private landUseStore: LandUseStore,
              private statisticStore: StatisticStore,
              private translationStore: TranslationStore,
              private fieldNutrientDistributionStore: FieldNutrientDistributionStore,
              private dateService: ApDateService,
              private soilGroupFormatService: SoilGroupFormatService,
              private soilSampleClassColorService: GetSoilSampleClassColorService,
              private soilSampleClassNamePipe: GetSoilSampleClassNamePipe) {
  }

  public generateHistogramStatistic(title: string, items: IFieldStatisticHistogramData[]): void {
    if (!items || items.length <= 0) {
      this.statisticStore.setStatisticData([], ApStatsType.Nutrients, '', title);
      return;
    }
    this.statisticStore.setStatisticData(this.getHistogramStatistic(items), ApStatsType.Nutrients, title, 'Nutrient__Select_SoilSampleField');
  }

  public getHistogramStatistic(items: IFieldStatisticHistogramData[]): IChartSet<IBarLineAreaChartData>[] {
    if (!items || items.length <= 0) {
      return [];
    }
    const areaSum = items.Reduce((target, e) => Create(e.AreaHa, 0) + target, 0);
    return [
      ...this._createStatistic('Nutrient__Band_ClassP', ChartSetValueType.CLASS, areaSum, items),
      ...this._createStatistic('Nutrient__Band_ClassK', ChartSetValueType.CLASS, areaSum, items),
      ...this._createStatistic('Nutrient__Band_ClassMg', ChartSetValueType.CLASS, areaSum, items),
      ...this._createStatistic('Nutrient__Band_ClassPh', ChartSetValueType.CLASS, areaSum, items),
      ...this._createStatistic('Nutrient__Band_SoilPlanningYear', ChartSetValueType.DATE, areaSum, items),
      ...this._createStatistic('Nutrient__Band_SoilGroup', ChartSetValueType.SOIL_GROUP, areaSum, items),
      ...this._createStatistic('Nutrient__Band_Region', ChartSetValueType.REGION, areaSum, items),
      ...this._createStatistic('Nutrient__Band_TypeOfUse', ChartSetValueType.LANDUSE, areaSum, items),
      ...this._createStatistic('Nutrient__Band_MethodP', ChartSetValueType.METHOD, areaSum, items),
      ...this._createStatistic('Nutrient__Band_MethodK', ChartSetValueType.METHOD, areaSum, items),
      ...this._createStatistic('Nutrient__Band_MethodMg', ChartSetValueType.METHOD, areaSum, items),
      ...this._createStatistic('Nutrient__Band_MethodPh', ChartSetValueType.METHOD, areaSum, items),
    ];
  }

  private _createStatistic(name: string, type: ChartSetValueType, areaSum: number, selected: IFieldStatisticHistogramData[]): IChartSet<IBarLineAreaChartData>[] {
    if (!selected || !selected.Any()) {
      return [];
    }
    let soilPlanningYears: { date: number, category: number }[] = [];
    let fieldSoilPlanningYears: { fieldId: IGuid, dates: { date: string, percent: number }[] }[] = [];
    if (type === ChartSetValueType.DATE) {
      soilPlanningYears = selected.Reduce((target, e) => {
        const hist = e.Histograms.Find(el => el.Key === name);
        if (!hist) {
          return target;
        }
        for (const val of hist.Values) {
          if (target.Any(el => el.category === e.Status && el.date === val)) {
            continue;
          }
          target.Add({category: e.Status, date: val});
        }
        return target;
      }, [] as { date: number, category: number }[]);
      fieldSoilPlanningYears = selected.Reduce((target, e) => {
        const hist = e.Histograms.Find(el => el.Key === name);
        if (!hist) {
          return target;
        }
        for (const key in hist.Values) {
          if (!hist.Values.hasOwnProperty(key)) {
            continue;
          }
          const year = hist.Values[key];
          const cells = hist.Counters[key];
          const dateName = this._mapDate(year);
          const found = target.FindIndex(t => t.fieldId === e.FieldId);
          if (found < 0) {
            target.Add({
              fieldId: e.FieldId,
              dates: [
                {
                  date: dateName,
                  percent: cells,
                },
              ],
            });
            continue;
          }
          if (!target[found].dates.Any(d => d.date === dateName)) {
            target[found].dates.Add({
              date: dateName,
              percent: cells,
            });
          }
        }
        return target;
      }, [] as { fieldId: IGuid, dates: { date: string, percent: number }[] }[]);
      for (const item of fieldSoilPlanningYears) {
        const allCells = item.dates.map(el => el.percent).Sum();
        for (const date of item.dates) {
          date.percent = date.percent * 100 / allCells;
        }
      }
    }
    let max = 0;
    if (type === ChartSetValueType.CLASS) {
      max = selected.Reduce((target, e) => {
        const counters = e.Histograms.Find(el => el.Key === name).Counters;
        for (const key in counters) {
          if (!counters.hasOwnProperty(key)) {
            continue;
          }
          if (counters[key] > 0 && counters.Sum() > 0) {
            const tmp = counters[key] / counters.Sum();
            target[key] += tmp * e.AreaHa;
          }
        }
        return target;
      }, new Array(selected[0].Histograms.Find(el => el.Key === name).Counters.length).fill(0))
        .Max();
      max = max + (20 * max / 100);
    }
    const res: IChartSet<IBarLineAreaChartData>[] = [];
    for (const dis of selected) {
      const hist = dis.Histograms.Find(e => e.Key === name);
      if (!hist) {
        continue;
      }
      res.Add({
        valueType: type,
        axes: this.soilSampleClassColorService.getStatisticAxesConfig(type, undefined, max),
        step: 1,
        data: {
          title: name,
          name,
          categories: hist.Values.Convert((e: any) => {
            switch (type) {
              case ChartSetValueType.CLASS:
                return this.soilSampleClassNamePipe.transform(e);
              case ChartSetValueType.REGION:
                return this._mapSampleRegion(e);
              case ChartSetValueType.METHOD:
                return this._mapSampleMethods(e);
              case ChartSetValueType.SOIL_GROUP:
                return this.soilGroupFormatService.mapSingle(e);
              case ChartSetValueType.LANDUSE:
                return this._mapLanduse(e);
              case ChartSetValueType.DATE:
                return dis.Status;
              default:
                return e.toString();
            }
          }),
          data: hist.Counters.Convert((e: any) => {
            switch (type) {
              case ChartSetValueType.CLASS:
                return {
                  value: dis && e > 0 ?
                    e / hist.Counters.Sum() * Create(dis.AreaHa, 0) :
                    0,
                } as IBarLineAreaChartDataValue;
              default:
                return {
                  value: e,
                } as IBarLineAreaChartDataValue;
            }
          }),
        },
        decimalPlaces: this.settings.FirstSetting.DigitsAfterDecimalPoint,
        color: (p) => {
          switch (type) {
            case ChartSetValueType.CLASS:
              return this.soilSampleClassColorService.getColor(p.category);
            case ChartSetValueType.DATE:
              switch (p) {
                case -1:
                  return '#980000';
                case 0:
                  return '#fdce3e';
                case 1:
                  return '#5ec232';
                default:
                  return undefined;
              }
            case ChartSetValueType.SOIL_GROUP:
              return this.soilGroupFormatService.colorCode(p);
            default:
              return undefined;
          }
        },
        meta: {
          fieldId: dis.FieldId,
          years: soilPlanningYears.map(item => {
            return {category: item.category, date: this._mapDate(item.date)};
          }),
          // die fieldSoilPlanningYears werden nur 1x ausgelesen → nur 1x im Statistik objekt halten
          // Objekt kann sonst sehr groß werden
          fieldYears: res.length === 0 ? fieldSoilPlanningYears : undefined,
          areaSum,
        }
      });
    }
    return res;
  }

  private _mapSampleMethods(value: number): string {
    const method = this.fieldNutrientDistributionStore.SampleMethods.Find(e => e.Id === value);
    if (!method) {
      return value.toString();
    }
    return this.translationStore.FindTranslationForSelectedLanguage(method.Method);
  }

  private _mapSampleRegion(value: number): string {
    const region = this.fieldNutrientDistributionStore.SampleRegions.Find(e => e.Id === value);
    if (!region) {
      return value.toString();
    }
    return this.translationStore.FindTranslationForSelectedLanguage(region.Region);
  }

  private _mapLanduse(value: number): string {
    const landuse = this.landUseStore.LandUse.Find(e => e.Id === value);
    if (!landuse) {
      return value.toString();
    }
    return this.translationStore.FindTranslationForSelectedLanguage(landuse.Key);
  }

  private _mapDate(value: number): string {
    return this.dateService.toFarmDate(new Date(parseInt(value.toString() + '000', 10))).format('YYYY');
  }
}
